import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { UseFormReset, UseFormSetValue, UseFormGetValues } from 'react-hook-form';
import { useFormDraft } from './useFormDraft';

interface DraftField {
  name: string;
  displayName: string;
  value: any;
  rawValue: any;
  isParticipants?: boolean;
  isEffects?: boolean;
}

interface DraftRestoreOptions {
  onRestoreStart?: () => void;
  onRestoreComplete?: () => void;
  excludeFields?: string[];
}

// Modify the hook to respect empty form type/id as a signal to disable draft functionality
export function useEnhancedFormDraft<T extends Record<string, any>>(
  formType: string,
  formId: string,
  {
    reset,
    setValue,
    getValues,
    isDirty
  }: {
    reset: UseFormReset<T>;
    setValue: UseFormSetValue<T>;
    getValues: UseFormGetValues<T>;
    isDirty: boolean;
  },
  options: DraftRestoreOptions = {}
) {
  // Early check if draft functionality should be disabled
  const isDraftEnabled = formType !== '' && formId !== '';

  // Get original functions but don't use them if drafts are disabled
  const { getDraft: originalGetDraft, clearDraft: originalClearDraft, startDelayedAutoSave: originalStartDelayedAutoSave, saveDraft: originalSaveDraft } = 
    useFormDraft(formType, formId, { excludeFields: options.excludeFields });

  // Create no-op versions of functions when drafts are disabled
  const getDraft = useCallback(() => {
    if (!isDraftEnabled) return null;
    return originalGetDraft();
  }, [isDraftEnabled, originalGetDraft]);

  const clearDraft = useCallback(() => {
    if (!isDraftEnabled) return;
    originalClearDraft();
  }, [isDraftEnabled, originalClearDraft]);

  const saveDraft = useCallback((data: any) => {
    if (!isDraftEnabled) return;
    originalSaveDraft(data);
  }, [isDraftEnabled, originalSaveDraft]);

  const startDelayedAutoSave = useCallback((getData: () => any, intervalMs: number = 30000) => {
    if (!isDraftEnabled) return () => { }; // Return no-op cleanup function
    return originalStartDelayedAutoSave(getData, intervalMs);
  }, [isDraftEnabled, originalStartDelayedAutoSave]);

  const [storedDraft, setStoredDraft] = useState<any>(null);
  const [isRestoringDraft, setIsRestoringDraft] = useState<boolean>(false);
  const draftRestorationRef = useRef<boolean>(false);
  const [shouldFetchPrice, setShouldFetchPrice] = useState<boolean>(false);
  const [restoredServiceId, setRestoredServiceId] = useState<number | null>(null);

  // Keep track of last save time to prevent too many toast notifications
  const lastSaveToastRef = useRef<number>(0);

  // Ref to store the interval ID for auto-saving
  const autoSaveIntervalRef = useRef<(() => void) | null>(null);

  // State to track if a draft was shown on form load
  const [draftShownOnLoad, setDraftShownOnLoad] = useState<boolean>(false);

  // State to track countdown for draft deletion
  const [draftDeleteCountdown, setDraftDeleteCountdown] = useState<number>(0);

  // Ref to store the interval ID for countdown timer
  const countdownIntervalRef = useRef<number | null>(null);

  // Ref to track if the user has edited the form after seeing a draft
  const userEditedAfterSeeingDraftRef = useRef<boolean>(false);

  // Check for drafts on mount and show dialog
  useEffect(() => {
    const draft = getDraft();
    if (draft) {
      console.log('Found draft on mount:', draft);
      setStoredDraft(draft);
      setDraftShownOnLoad(true);
    }
  }, [getDraft]);

  // Set up auto-save on form changes when dirty
  useEffect(() => {
    // Skip this effect entirely if drafts are disabled
    if (!isDraftEnabled) return;

    // Clear any previous auto-save cleanup
    if (autoSaveIntervalRef.current) {
      autoSaveIntervalRef.current();
      autoSaveIntervalRef.current = null;
    }

    // Set up new auto-save if form is dirty and not restoring
    if (isDirty && !isRestoringDraft) {
      // Only start countdown if this is first edit after seeing draft AND counting isn't already happening
      if (draftShownOnLoad && userEditedAfterSeeingDraftRef.current === false && draftDeleteCountdown === 0) {
        userEditedAfterSeeingDraftRef.current = true;
        // Start countdown to delete the old draft
        setDraftDeleteCountdown(60);
      }

      // Start delayed auto-save
      const cleanupFn = startDelayedAutoSave(() => {
        const currentValues = getValues();
        // Skip auto-save if we're in the middle of draft restoration
        if (draftRestorationRef.current) return currentValues;

        // Show toast notification occasionally
        const now = Date.now();
        if (now - lastSaveToastRef.current > 60000) { // Show toast at most once per minute
          toast.info('Automatyczne zapisywanie wersji roboczej...', {
            autoClose: 2000,
            position: toast.POSITION.BOTTOM_RIGHT,
          });
          lastSaveToastRef.current = now;

          // Update stored draft after saving
          setTimeout(() => {
            const refreshedDraft = getDraft();
            if (refreshedDraft) {
              setStoredDraft(refreshedDraft);
            }
          }, 500);
        }

        return currentValues;
      }, 30000); // Make sure we use 30 seconds consistently

      // Store the cleanup function
      autoSaveIntervalRef.current = cleanupFn;
    }

    // Clean up on unmount or when dependencies change
    return () => {
      if (autoSaveIntervalRef.current) {
        autoSaveIntervalRef.current();
        autoSaveIntervalRef.current = null;
      }
    };
  }, [isDraftEnabled, isDirty, isRestoringDraft, getValues, startDelayedAutoSave, draftShownOnLoad, getDraft, draftDeleteCountdown]);

  // Clean up the countdown functionality and fix multiple timers

  // Remove duplicate countdown useEffect - we have two countdown effects that conflict
  // Keep only this one and improve it:

  // Set up countdown timer for draft deletion
  useEffect(() => {
    // Skip this effect entirely if drafts are disabled
    if (!isDraftEnabled) return;

    // Only run countdown if we need to
    if (draftDeleteCountdown > 0) {
      // Clear any existing interval
      if (countdownIntervalRef.current) {
        window.clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }

      // console.log(`Starting countdown from ${draftDeleteCountdown} seconds`);

      // Set up countdown interval
      countdownIntervalRef.current = window.setInterval(() => {
        setDraftDeleteCountdown(prevCount => {
          const newCount = prevCount - 1;
          // console.log(`Countdown: ${newCount} seconds remaining`);

          // When countdown reaches 0, clear the old draft
          if (newCount <= 0) {
            clearDraft();
            setStoredDraft(null);

            // Only show toast if we actually had a draft
            if (storedDraft) {
              toast.info('Poprzednia wersja robocza została usunięta', {
                autoClose: 2000,
                position: toast.POSITION.BOTTOM_RIGHT,
              });
            }

            // Clear the interval
            if (countdownIntervalRef.current) {
              window.clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }

            // Reset draft flags so auto-save can start again
            setDraftShownOnLoad(false);
            userEditedAfterSeeingDraftRef.current = false;

            // Start a new auto-save after a short delay if form is dirty
            if (isDirty) {
              setTimeout(() => {
                // If the form is still dirty, trigger a manual save to start the process
                const currentValues = getValues();
                saveDraft(currentValues);

                // Update stored draft ref
                setTimeout(() => {
                  const refreshedDraft = getDraft();
                  if (refreshedDraft) {
                    setStoredDraft(refreshedDraft);
                    toast.info('Zapisano nową wersję roboczą', {
                      autoClose: 2000,
                      position: toast.POSITION.BOTTOM_RIGHT,
                    });
                  }
                }, 500);
              }, 1000);
            }
          }

          return newCount > 0 ? newCount : 0;
        });
      }, 1000);

      // Clean up on unmount or dependency change
      return () => {
        if (countdownIntervalRef.current) {
          window.clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
      };
    }
  }, [isDraftEnabled, draftDeleteCountdown, clearDraft, storedDraft, isDirty, getValues, saveDraft, getDraft]);

  const resetCountdown = useCallback(() => {
    if (!isDraftEnabled) return;
    
    console.log('Resetting draft delete countdown to 60 seconds');
    setDraftDeleteCountdown(60);
  }, [isDraftEnabled]);

  // The main restore function
  const handleRestoreDraft = useCallback((draftData: any) => {
    // Don't do anything if draft functionality is disabled
    if (!isDraftEnabled) return;
    
    console.log('Restoring draft:', draftData);

    if (draftData) {
      if (options.onRestoreStart) options.onRestoreStart();

      // Set both the state and the ref
      setIsRestoringDraft(true);
      draftRestorationRef.current = true;

      try {
        // Extract serviceId
        const serviceId = draftData.serviceId ?
          parseInt(draftData.serviceId.toString()) : 0;

        console.log('Draft has serviceId:', serviceId);

        // Store serviceId for price fetching
        if (serviceId && serviceId > 0) {
          setRestoredServiceId(serviceId);
        }

        // Process the draft data - extract everything except serviceId
        const formValues: Partial<T> = { ...draftData };

        // Reset form with processed values
        reset(formValues as T);

        // Set serviceId separately after a delay
        setTimeout(() => {
          if (serviceId && serviceId > 0) {
            console.log('Setting serviceId in form:', serviceId);

            setValue('serviceId' as any, serviceId as any, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true
            });

            // Flag that we should fetch the price
            setShouldFetchPrice(true);
          }

          toast.success('Wczytano wersję roboczą formularza');

          // Keep the ref true for a bit longer to ensure any async effects don't fire
          setTimeout(() => {
            draftRestorationRef.current = false;
            setIsRestoringDraft(false);

            // Reset draft state to allow new auto-saving to start
            setDraftShownOnLoad(false);
            userEditedAfterSeeingDraftRef.current = false;

            // Start countdown to delete draft after 60 seconds
            setDraftDeleteCountdown(60);

            if (options.onRestoreComplete) options.onRestoreComplete();
          }, 500);
        }, 100);
      } catch (error) {
        console.error('Error restoring draft:', error);
        toast.error('Wystąpił błąd podczas przywracania wersji roboczej');
        draftRestorationRef.current = false;
        setIsRestoringDraft(false);
        if (options.onRestoreComplete) options.onRestoreComplete();
      }
    }
  }, [reset, setValue, options, setShouldFetchPrice, setRestoredServiceId, setDraftDeleteCountdown, isDraftEnabled]);

  // Field name mapping for better readability using useMemo to prevent recreating on every render
  const fieldNameMap = useCallback((): Record<string, string> => ({
    serviceId: "ID usługi",
    serviceName: "Nazwa usługi",
    customeClientServiceName: "Niestandardowa nazwa",
    preposedDateTime: "Proponowana data",
    placeOfServiceAndAnotherInformations: "Miejsce",
    clientComment: "Komentarz",
    trainingProgram: "Program szkolenia",
    educationEffects: "Efekty kształcenia",
    payerClientName: "Nazwa klienta",
    payerClientAddress: "Adres klienta",
    payerClientNIP: "NIP klienta",
    payerInstitutionName: "Nazwa instytucji",
    payerLastName: "Nazwisko płatnika",
    payerFirstName: "Imię płatnika",
    payerStreet: "Ulica",
    payerBuilding: "Budynek",
    payerPremises: "Nr lokalu",
    payerZip: "Kod pocztowy",
    payerCity: "Miasto",
    payerState: "Województwo",
    payerCountry: "Kraj",
    payerNIP: "NIP",
    rows: "Uczestnicy",
    totalItem: "Liczba uczestników"
  }), []);

  // Update getDraftSummary method with better field naming and formatting
  const getDraftSummary = useCallback((draft: any): { draftDate: string; fields: DraftField[] } => {
    if (!isDraftEnabled || !draft) return { draftDate: '', fields: [] };
    
    const now = new Date();
    let date = '';
    try {
      // Try to get the timestamp from the draft
      if (draft?.timestamp) {
        date = new Date(draft.timestamp).toLocaleString();
      } else {
        date = now.toLocaleString();
      }
    } catch {
      date = now.toLocaleString();
    }

    // Get the field name map
    const nameMap = fieldNameMap();

    // Extract key fields, with less aggressive filtering
    const fields = Object.entries(draft || {})
      .filter(([key, value]) =>
        // Only filter out internal properties and completely empty values
        !key.startsWith('__') &&
        key !== 'timestamp' &&
        value !== undefined &&
        value !== null &&
        // Don't filter out empty strings anymore
        !(Array.isArray(value) && value.length === 0)
      )
      .map(([name, value]): DraftField => {
        // Use readable field name if available
        const displayName = nameMap[name] || name;

        // Format special fields
        if (name === 'preposedDateTime' && typeof value === 'string') {
          try {
            return {
              name,
              displayName,
              value: new Date(value).toLocaleString(),
              rawValue: value
            };
          } catch {
            return { name, displayName, value: value, rawValue: value };
          }
        }

        // Format participant rows
        if (name === 'rows' && Array.isArray(value) && value.length > 0) {
          return {
            name,
            displayName,
            value: `${value.length} uczestników`,
            rawValue: value,
            isParticipants: true
          };
        }

        // Format education effects
        if (name === 'educationEffects' && typeof value === 'string') {
          const effects = value.split(';').filter(effect => effect.trim());
          if (effects.length > 0) {
            return {
              name,
              displayName,
              value: `${effects.length} efektów kształcenia`,
              rawValue: effects,
              isEffects: true
            };
          }
        }

        // Default case
        return {
          name,
          displayName,
          value: value,
          rawValue: value
        };
      })
      .filter((field): field is DraftField => field !== null && field.value !== undefined);

    return { draftDate: date, fields };
  }, [fieldNameMap, isDraftEnabled]);

  // Enhance clearDraft to also update component state
  const enhancedClearDraft = useCallback(() => {
    // Don't do anything if draft functionality is disabled
    if (!isDraftEnabled) return;

    clearDraft();
    setStoredDraft(null);
    setDraftDeleteCountdown(0);

    // Reset draft state variables to enable a fresh auto-save cycle
    setDraftShownOnLoad(false);
    userEditedAfterSeeingDraftRef.current = false;

    console.log('Draft cleared from memory and UI');
  }, [clearDraft, isDraftEnabled]);  // Add isDraftEnabled to dependencies

  return {
    storedDraft: isDraftEnabled ? storedDraft : null,
    isRestoringDraft,
    draftRestorationRef,
    shouldFetchPrice,
    restoredServiceId,
    setShouldFetchPrice,
    setRestoredServiceId,
    handleRestoreDraft,
    getDraft,
    clearDraft: enhancedClearDraft,
    saveDraft,
    draftShownOnLoad: isDraftEnabled ? draftShownOnLoad : false,
    draftDeleteCountdown: isDraftEnabled ? draftDeleteCountdown : 0,
    getDraftSummary,
    resetCountdown,
    setDraftDeleteCountdown
  };
}